import React, { useState, useRef, useCallback } from "react";
import { RotateCw, Crosshair, Move } from "lucide-react";

// Real-world reference width (inches) of each mockup object's sticker surface.
const MOCKUPS = {
  laptop: { label: "Laptop", realWidthIn: 14.01, maxPct: 90, objW: "min(80%, 470px)", objAspect: 1.43 },
  bottle: { label: "Bottle", realWidthIn: 2.6, maxPct: 74, objW: "min(30%, 150px)", objAspect: 0.34 },
  notebook: { label: "Notebook", realWidthIn: 5.8, maxPct: 80, objW: "min(52%, 300px)", objAspect: 0.72 },
  phone: { label: "Phone", realWidthIn: 2.8, maxPct: 70, objW: "min(26%, 132px)", objAspect: 0.48 },
};

const maskStyle = (url) => ({
  WebkitMaskImage: `url(${url})`,
  maskImage: `url(${url})`,
  WebkitMaskSize: "contain",
  maskSize: "contain",
  WebkitMaskRepeat: "no-repeat",
  maskRepeat: "no-repeat",
  WebkitMaskPosition: "center",
  maskPosition: "center",
});

function DimensionRuler({ widthIn, heightIn }) {
  const line = "rgba(10,10,10,0.55)";
  return (
    <div className="absolute inset-0 pointer-events-none" data-testid="mockup-dimensions">
      <div className="absolute -top-6 left-0 right-0 flex items-center">
        <div className="flex-1 h-px" style={{ background: line }} />
        <span className="mx-1 px-1.5 py-0.5 rounded bg-white/90 border border-black/40 text-[10px] font-head font-bold leading-none">{Number(widthIn).toFixed(1)}"</span>
        <div className="flex-1 h-px" style={{ background: line }} />
      </div>
      <div className="absolute -top-[30px] left-0 w-px h-3" style={{ background: line }} />
      <div className="absolute -top-[30px] right-0 w-px h-3" style={{ background: line }} />
      <div className="absolute -left-6 top-0 bottom-0 flex flex-col items-center">
        <div className="flex-1 w-px" style={{ background: line }} />
        <span className="my-1 px-1.5 py-0.5 rounded bg-white/90 border border-black/40 text-[10px] font-head font-bold leading-none">{Number(heightIn).toFixed(1)}"</span>
        <div className="flex-1 w-px" style={{ background: line }} />
      </div>
      <div className="absolute -left-[30px] top-0 h-px w-3" style={{ background: line }} />
      <div className="absolute -left-[30px] bottom-0 h-px w-3" style={{ background: line }} />
    </div>
  );
}

function LaptopObject({ innerRef, children }) {
  return (
    <div ref={innerRef} className="relative rounded-[20px] border-[3px] border-[#3a3c42] overflow-hidden" style={{ width: MOCKUPS.laptop.objW, aspectRatio: `${MOCKUPS.laptop.objAspect}`, background: "linear-gradient(135deg, #a7a9ad 0%, #8f9195 45%, #7c7e83 100%)", boxShadow: "0 14px 24px -18px rgba(0,0,0,0.5)" }}>
      <div className="absolute inset-0 rounded-[16px] opacity-40" style={{ background: "repeating-linear-gradient(90deg, rgba(255,255,255,0.05) 0 1px, rgba(0,0,0,0.04) 1px 2px)" }} />
      {children}
    </div>
  );
}

function NotebookObject({ innerRef, children }) {
  return (
    <div ref={innerRef} className="relative rounded-r-[10px] rounded-l-[4px] border-[3px] border-[#2b2b2b] overflow-hidden" style={{ width: MOCKUPS.notebook.objW, aspectRatio: `${MOCKUPS.notebook.objAspect}`, background: "linear-gradient(135deg, #2f3440 0%, #232733 100%)", boxShadow: "0 14px 24px -18px rgba(0,0,0,0.5)" }}>
      <div className="absolute left-[6px] top-0 bottom-0 w-[6px] bg-black/30 rounded pointer-events-none" />
      {children}
    </div>
  );
}

function PhoneObject({ innerRef, children }) {
  return (
    <div className="relative rounded-[26px] border-[3px] border-[#2b2d33]" style={{ width: MOCKUPS.phone.objW, aspectRatio: `${MOCKUPS.phone.objAspect}`, background: "linear-gradient(150deg, #23252c 0%, #34373f 50%, #1d1f25 100%)", boxShadow: "0 12px 22px -16px rgba(0,0,0,0.55)", padding: "7px" }}>
      <div ref={innerRef} className="relative w-full h-full rounded-[18px] overflow-hidden" style={{ background: "linear-gradient(160deg, #cfd6e6 0%, #eef2f8 45%, #dfe4ee 100%)" }}>
        <div className="absolute top-1 left-1/2 -translate-x-1/2 w-8 h-1.5 rounded-full bg-[#2b2d33] z-10 pointer-events-none" />
        {children}
      </div>
    </div>
  );
}

function BottleObject({ innerRef, children }) {
  return (
    <div className="relative flex flex-col items-center" style={{ width: MOCKUPS.bottle.objW }}>
      <div className="w-[34%] h-4 rounded-t-md bg-[#3a3c42]" />
      <div className="w-[52%] h-3 bg-[#4a4d54]" />
      <div ref={innerRef} className="relative w-full rounded-[26px] border-[3px] border-[#3a3c42] overflow-hidden" style={{ aspectRatio: `${MOCKUPS.bottle.objAspect}`, background: "linear-gradient(100deg, #cfd2d6 0%, #eef0f2 22%, #b9bcc1 55%, #d7dade 100%)", boxShadow: "0 12px 22px -16px rgba(0,0,0,0.45)" }}>
        {children}
      </div>
    </div>
  );
}

export default function MockupView({ stickerSrc, isHolo, isChrome, widthIn, heightIn, mockup }) {
  const cfg = MOCKUPS[mockup] || MOCKUPS.laptop;
  const ratio = widthIn && heightIn ? Number(widthIn) / Number(heightIn) : 1;
  const rawPct = (Number(widthIn) / cfg.realWidthIn) * 100;
  const clamped = rawPct > cfg.maxPct;
  const wPct = Math.min(rawPct, cfg.maxPct);

  const [rotation, setRotation] = useState(0);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const objRef = useRef(null);
  const stickerRef = useRef(null);
  const drag = useRef(null);

  const reset = () => {
    setRotation(0);
    setPos({ x: 0, y: 0 });
  };

  const onPointerDown = useCallback((e) => {
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    drag.current = { px: e.clientX, py: e.clientY, x: pos.x, y: pos.y };
  }, [pos]);

  const onPointerMove = useCallback((e) => {
    if (!drag.current) return;
    let nx = drag.current.x + (e.clientX - drag.current.px);
    let ny = drag.current.y + (e.clientY - drag.current.py);
    const obj = objRef.current?.getBoundingClientRect();
    const st = stickerRef.current?.getBoundingClientRect();
    if (obj && st) {
      const maxX = Math.max((obj.width - st.width) / 2 + st.width * 0.4, 8);
      const maxY = Math.max((obj.height - st.height) / 2 + st.height * 0.4, 8);
      nx = Math.max(-maxX, Math.min(maxX, nx));
      ny = Math.max(-maxY, Math.min(maxY, ny));
    }
    setPos({ x: nx, y: ny });
  }, []);

  const onPointerUp = useCallback((e) => {
    drag.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
  }, []);

  const Obj = { laptop: LaptopObject, notebook: NotebookObject, bottle: BottleObject, phone: PhoneObject }[mockup] || LaptopObject;

  const stickerNode = (
    <div
      ref={stickerRef}
      data-testid="mockup-draggable"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      className="absolute left-1/2 top-1/2 cursor-grab active:cursor-grabbing touch-none select-none"
      style={{
        width: `${wPct}%`,
        aspectRatio: `${ratio}`,
        transform: `translate(calc(-50% + ${pos.x}px), calc(-50% + ${pos.y}px)) rotate(${rotation}deg)`,
      }}
    >
      <DimensionRuler widthIn={widthIn} heightIn={heightIn} />
      <img src={stickerSrc} alt="sticker on product" draggable={false} className="w-full h-full object-contain drop-shadow-[1px_2px_2px_rgba(0,0,0,0.32)] pointer-events-none" data-testid="mockup-sticker-img" />
      {isHolo && <div className="holo-sheen absolute inset-0 pointer-events-none" style={{ ...maskStyle(stickerSrc), mixBlendMode: "screen", opacity: 0.55 }} />}
      {isChrome && <div className="chrome-sheen absolute inset-0 pointer-events-none" style={{ ...maskStyle(stickerSrc), mixBlendMode: "screen", opacity: 0.55 }} />}
    </div>
  );

  return (
    <div data-testid="sticker-stage" className="relative w-full h-full min-h-[420px] rounded-2xl overflow-hidden flex items-center justify-center" style={{ background: "radial-gradient(120% 100% at 50% 0%, #ffffff 0%, #eef1f5 55%, #e4e8ee 100%)" }}>
      <div className="flex items-center justify-center w-full h-full p-12">
        <Obj innerRef={objRef}>{stickerNode}</Obj>
      </div>

      <div className="absolute top-3 right-3 flex items-center gap-2 bg-white/92 backdrop-blur nb-border rounded-full px-3 py-2 nb-shadow-sm">
        <RotateCw size={15} />
        <input data-testid="mockup-rotate" type="range" min="-180" max="180" value={rotation} onChange={(e) => setRotation(Number(e.target.value))} className="w-24 accent-nbpink" />
        <span className="text-[11px] font-head font-bold w-8 text-right">{rotation}°</span>
        <button data-testid="mockup-reset" onClick={reset} title="Reset position" className="ml-1 p-1 rounded-full hover:bg-black/5">
          <Crosshair size={15} />
        </button>
      </div>

      <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur nb-border rounded-full px-3 py-1 text-xs font-head font-bold flex items-center gap-1.5">
        <Move size={13} /> Drag to place · {Number(widthIn).toFixed(1)}" × {Number(heightIn).toFixed(1)}" on a {cfg.label.toLowerCase()}
      </div>
      {clamped && (
        <div data-testid="mockup-scale-warning" className="absolute bottom-3 right-3 bg-nbyellow nb-border rounded-full px-3 py-1 text-xs font-head font-bold">
          Bigger than this {cfg.label.toLowerCase()}!
        </div>
      )}
    </div>
  );
}
